import { useState, useEffect, useCallback } from 'react';
import { api } from '../../../lib/api.js';

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function parseKeywords(input) {
  const seen = new Set();
  const out = [];
  for (const part of input.split(',')) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
}

export default function AdminKeywords() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addInput, setAddInput] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get('/blackouts');
      setRows(data.blackouts || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const keywords = parseKeywords(addInput);
    if (keywords.length === 0) return;
    setAdding(true);
    setError(null);
    try {
      await api.post('/blackouts', { keywords });
      setAddInput('');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (row) => {
    if (!window.confirm(`Remove blackout for "${row.keyword}"?`)) return;
    setError(null);
    try {
      await api.del(`/blackouts/${row._id}`);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <>
      <div className="mb-6">
        <h2 className="font-display text-[14px] font-bold tracking-display-md uppercase text-hxd-ink">
          Blackout Keywords
        </h2>
        <p className="text-[13px] text-hxd-muted mt-1 max-w-2xl">
          Articles matching these keywords are filtered during pipeline stage 1.5. Keywords are
          also added when you cut an article in the editor.
        </p>
      </div>

      {error && (
        <div className="mb-5 flex items-start justify-between gap-3 bg-red-50 border border-red-200 rounded p-3">
          <p className="text-hxd-err text-[13px]">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-hxd-muted hover:text-hxd-err text-lg leading-none"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      <form onSubmit={handleAdd} className="hxd-card mb-6">
        <div className="hxd-card-header">Add keywords</div>
        <div className="flex flex-col sm:flex-row gap-2 items-end">
          <div className="flex-1 w-full">
            <label className="hxd-label">Keywords (comma-separated)</label>
            <input
              className="hxd-input"
              value={addInput}
              onChange={(e) => setAddInput(e.target.value)}
              placeholder="Boeing, layoffs, bankruptcy"
            />
          </div>
          <button
            type="submit"
            className="hxd-btn hxd-btn-primary hxd-btn-sm h-fit"
            disabled={adding || !addInput.trim()}
          >
            {adding ? 'Adding…' : 'Add'}
          </button>
        </div>
      </form>

      {loading ? (
        <p className="text-hxd-muted text-[13px]">Loading keywords…</p>
      ) : rows.length === 0 ? (
        <div className="hxd-card">
          <p className="text-hxd-muted text-[13px]">No active blackout keywords.</p>
        </div>
      ) : (
        <div className="hxd-card p-0 overflow-x-auto">
          <table className="w-full text-[13px] min-w-[640px]">
            <thead>
              <tr className="border-b-2 border-hxd-orange bg-hxd-surface">
                {['Keyword', 'Added from', 'Added by', 'Date', 'Actions'].map((h) => (
                  <th
                    key={h}
                    className="font-display text-[10px] font-bold tracking-display-md uppercase text-left px-4 py-3"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row._id}
                  className="border-b border-hxd-border last:border-0 hover:bg-hxd-surface"
                >
                  <td className="px-4 py-3 font-semibold">{row.keyword}</td>
                  <td className="px-4 py-3 text-hxd-muted max-w-[240px]">
                    {row.addedFromArticle?.headline ? (
                      row.addedFromArticle.sourceUrl ? (
                        <a
                          href={row.addedFromArticle.sourceUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="hover:text-hxd-orange line-clamp-2"
                        >
                          {row.addedFromArticle.headline}
                        </a>
                      ) : (
                        <span className="line-clamp-2">{row.addedFromArticle.headline}</span>
                      )
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-3 text-hxd-muted whitespace-nowrap">
                    {row.addedByName || '—'}
                  </td>
                  <td className="px-4 py-3 text-hxd-muted whitespace-nowrap">
                    {fmtDate(row.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      className="hxd-btn hxd-btn-ghost hxd-btn-sm text-hxd-err border-transparent"
                      onClick={() => handleRemove(row)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
